// HU-12 · HU-15 · HU-43 · la evaluación del Comité: investigación punto por punto,
// causalidad y conclusiones, gravedad, y los especialistas invitados. F-03, Sprint 2.
//
// UNA LECTURA ARMA LA PANTALLA: `GET /evaluacion/{caso}` trae el instructivo respondido,
// la causalidad, la gravedad, el algoritmo preseleccionado (RN-18), las invitaciones y los
// aportes de los especialistas, y `puede_editar` —que es lo que decide si la pantalla es
// la del Comité o la de solo lectura (invitado, AR, aprobador; HU-11-C21 / HU-43-C7).
//
// LOS VALORES DEL INSTRUCTIVO VIAJAN EN CÓDIGO (`si` / `no` / `no_aplica`); la etiqueta
// («Sí», «Documentado»…) es de la pantalla. La versión del algoritmo NO viaja: la copia el
// servidor del catálogo (RN-14).

import { pedir, pedirBinario, segUuid } from './cliente';

export type ValorDeRespuesta = 'si' | 'no' | 'no_aplica';

export type RespuestaDelInstructivo = {
  punto: number;
  valor: ValorDeRespuesta;
  observacion: string | null;
  derivada: boolean;
  observacion_protegida: boolean;
};

export type Invitacion = {
  id: string;
  nombre: string;
  invitado_por: string;
  invitado_en: string;
  retirado_en: string | null;
  participo_en_el_analisis: boolean;
  vigente: boolean;
  correo: string | null;
};

export type Aporte = {
  id: string;
  caso_id: string;
  invitacion_id: string;
  nombre: string;
  tipo: 'observacion' | 'documento';
  registrado_en: string;
  /**
   * HU-43-C9/C10. Un aporte RETIRADO llega sin `texto` y sin `archivo_nombre`: el servidor
   * los omite a propósito, porque retirar es dejar de ponerlo a disposición del Comité. De
   * lo retirado solo se sabe quién aportó, cuándo, y que lo retiró (y con qué fecha).
   */
  texto: string | null;
  archivo_nombre: string | null;
  /** RN-09: el documento del especialista trae datos de personas (L2). Opcional: el backend lo expone desde el 14-sep-2026. */
  contiene_dato_de_personas?: boolean;
  /** Cuándo se retiró; `null` o ausente mientras sigue a disposición del Comité (22-sep-2026). */
  retirado_en?: string | null;
  retirado_por?: string | null;
  /** El motivo es OPCIONAL: se retira con o sin explicación. */
  motivo_del_retiro?: string | null;
  /**
   * Si QUIEN MIRA puede retirarlo. Lo decide el servidor, y solo es cierto para el AUTOR:
   * el Comité no retira el aporte de un tercero. Opcional: un despliegue anterior no lo
   * manda, y sin el campo no se ofrece el botón (no se adivina el permiso).
   */
  puede_retirarlo?: boolean;
};

/** Lo que le falta a la evaluacion para que el FAR-03 se pueda elaborar (opcional: el backend lo manda cuando lo calcula). */
export type FaltanteDelInforme = { campo: string; seccion: string; detalle: string; bloqueante: boolean };

/** HU-13: un compromiso del Comité. `seccion_del_informe` 7 = «Solución inmediata», 8 = «Solución preventiva» (HU-14-C2). */
export type Accion = {
  id: string;
  tipo: string;
  que_se_hace: string;
  responsable: string;
  plazo: string;
  estado: 'pendiente' | 'en_curso' | 'cumplida';
  seccion_del_informe: number;
  observacion: string | null;
};

export type Evaluacion = {
  caso_id: string;
  evaluacion_id: string;
  gravedad: 'grave' | 'no_grave' | null;
  criterios_gravedad: string[];
  grado_causalidad: string | null;
  algoritmo: string | null;
  version_algoritmo: string | null;
  justificacion: string | null;
  conclusiones: string | null;
  respondidos: number;
  total: number;
  pendientes: number[];
  respuestas: RespuestaDelInstructivo[];
  participantes: string[];
  ampliacion_pendiente: boolean;
  /** HU-49-C5, literal: «Falta la información adicional de la actividad 8»; null si no aplica. */
  aviso_de_ampliacion: string | null;
  criterio_preliminar_del_registrador: string | null;
  algoritmo_predeterminado: string | null;
  constancia_del_algoritmo: string | null;
  /**
   * HU-12 / HU-14 (QA, 18-sep-2026): topes de caracteres leídos de `parametro`
   * (`evaluacion_limites_de_texto`): `justificacion_caracteres`, `conclusion_caracteres`,
   * `redaccion_seccion_caracteres`. Los aplica la pantalla como `maxLength` + contador y
   * el backend los exige al guardar. Sin la fila no hay tope (como el formulario de registro).
   */
  limites_de_texto?: Record<string, number>;
  /**
   * HU-49-C2/C10 · qué acepta el botón «Seleccionar archivo» del sustento, leído de las
   * MISMAS filas de `parametro` que la evidencia del caso (`adjunto_extensiones_bloqueadas`
   * y `adjunto_tamano_maximo_mb`). Viaja con la evaluación —el mismo canal que ya trae los
   * topes de texto— para que la pantalla no ofrezca lo que el servidor va a rechazar.
   */
  anexo_extensiones_bloqueadas?: string[];
  anexo_tamano_maximo_mb?: number | null;
  ampliacion_habilitada: boolean;
  incluir_en_proximo_ips: boolean;
  aportes_de_especialistas: Aporte[];
  invitaciones: Invitacion[];
  acciones: Accion[];
  puede_editar: boolean;
  /** C1/C2 (21-sep-2026): qué sigue editable cuando `puede_editar` es falso (caso fuera del Comité). */
  puede_ampliar?: boolean;
  puede_seguir_acciones?: boolean;
  /** HU-43-C4: retirar a un invitado es una accion de seguridad; no se congela con el informe en aprobacion. */
  puede_retirar_especialistas?: boolean;
  numero_de_caso: string | null;
  pais_evento: string | null;
  estado_del_caso: string | null;
  faltantes_para_el_informe?: FaltanteDelInforme[];
  /**
   * BUG-898 (AT S3 §2.2.3): por qué la evaluación no se edita, derivado del ESTADO DEL
   * INFORME («El informe FAR-03 de PE-231-2026 está en aprobación: …»). Es el mismo texto del
   * 422 de las doce escrituras congeladas. Opcional: lo trae la tarea 898-2 del backend; sin
   * él la pantalla conserva el aviso que ya armaba.
   */
  motivo_de_no_editable?: string | null;
};

/**
 * FUSIONA la respuesta de una mutacion con la evaluacion que ya estaba en pantalla (D2).
 *
 * Las respuestas de `PATCH /instructivo`, `PUT /causalidad`, `PUT /gravedad`… venian
 * SIN la cabecera del caso (`numero_de_caso`, `pais_evento`, `estado_del_caso`) ni con el
 * predeterminado, la constancia, las invitaciones o los aportes: al hacer `setEvaluacion`
 * con lo que llegaba, la cabecera se vaciaba y «Algoritmo aplicado» perdia la preseleccion
 * hasta reabrir la evaluacion (observado en la E2E del 12-sep-2026). Lo que la nueva trae
 * null o vacio se conserva de la previa; lo que trae, manda. El backend ya devuelve el
 * DTO completo, pero el front se defiende igual.
 */
export function fusionar(previa: Evaluacion | null, nueva: Evaluacion): Evaluacion {
  if (!previa) return nueva;
  const conserva = <K extends keyof Evaluacion>(clave: K): Evaluacion[K] => {
    const valor = nueva[clave];
    const vacio = valor === null || valor === undefined || (Array.isArray(valor) && valor.length === 0);
    return vacio ? previa[clave] : valor;
  };
  return {
    ...nueva,
    numero_de_caso: conserva('numero_de_caso'),
    pais_evento: conserva('pais_evento'),
    estado_del_caso: conserva('estado_del_caso'),
    algoritmo_predeterminado: conserva('algoritmo_predeterminado'),
    constancia_del_algoritmo: conserva('constancia_del_algoritmo'),
    // Los topes son un objeto: `{}` cuenta como vacío y se conserva el de la previa.
    limites_de_texto: Object.keys(nueva.limites_de_texto ?? {}).length ? nueva.limites_de_texto : previa.limites_de_texto,
    anexo_extensiones_bloqueadas: nueva.anexo_extensiones_bloqueadas?.length ? nueva.anexo_extensiones_bloqueadas : previa.anexo_extensiones_bloqueadas,
    anexo_tamano_maximo_mb: nueva.anexo_tamano_maximo_mb ?? previa.anexo_tamano_maximo_mb,
    ampliacion_pendiente: nueva.ampliacion_pendiente ?? previa.ampliacion_pendiente,
    aviso_de_ampliacion: conserva('aviso_de_ampliacion'),
    invitaciones: conserva('invitaciones'),
    aportes_de_especialistas: conserva('aportes_de_especialistas'),
    puede_editar: nueva.puede_editar ?? previa.puede_editar,
    puede_ampliar: nueva.puede_ampliar ?? previa.puede_ampliar,
    puede_seguir_acciones: nueva.puede_seguir_acciones ?? previa.puede_seguir_acciones,
    puede_retirar_especialistas: nueva.puede_retirar_especialistas ?? previa.puede_retirar_especialistas,
  };
}

/**
 * El techo del esquema HTTP para una sección redactable del FAR-03 (`RedaccionEntrada.texto`,
 * HU-14). El parámetro puede bajarlo, no subirlo: si no hay fila, este es el tope real.
 */
export const TOPE_DE_SECCION_DEL_ESQUEMA = 20000;

/**
 * QA 01-10-2026 (B9): el tope de los textos libres del Comité que no tienen fila en
 * `parametro`: «Sustento» y «Justificación del cambio» de la gravedad y «Observaciones» de
 * una acción. Es el del esquema HTTP (`TOPE_DE_TEXTO_LIBRE_DEL_COMITE` en `esquemas.py`,
 * `max_length=2000`), como «Qué se subsanó».
 */
export const TOPE_DE_TEXTO_LIBRE_DEL_COMITE = 2000;

/** El tope configurado para un campo, o `undefined` si no hay (sin tope, sin contador). */
export const topeDeTexto = (evaluacion: Pick<Evaluacion, 'limites_de_texto'> | null | undefined, clave: string): number | undefined => {
  const tope = evaluacion?.limites_de_texto?.[clave];
  return typeof tope === 'number' && tope > 0 ? tope : undefined;
};

/** El mensaje bajo el campo cuando lo escrito supera el tope (mismo texto que el backend). */
export const mensajeDeTopeSuperado = (campo: string, largo: number, tope: number): string =>
  `${campo} supera el máximo de ${tope} caracteres (tiene ${largo}).`;

export type GuiaDeGravedad = {
  criterios: string[];
  criterio_preliminar_del_registrador: string | null;
  /** C5 (22-sep-2026): quién opinó — «Criterio del registrador: grave (Dr. Torres)» (HU-15-C3). */
  criterio_preliminar_de?: string | null;
  gravedad_decidida: string | null;
  aviso?: string;
};

export type EspecialistaSugerido = {
  usuario_id: string;
  nombre: string;
  correo: string | null;
  cargo: string | null;
  paises: string[];
  del_pais_del_evento: boolean;
};

export type InvitacionPropia = {
  invitacion_id: string;
  caso_id: string;
  numero: string | null;
  pais_evento: string;
  estado_del_caso: string;
  invitado_por: string;
  invitado_en: string;
  vigente: boolean;
  puede_abrirlo: boolean;
};

/** HU-12-C1: las cinco opciones, exactamente como las escribe el Formato Amarillo (RN-08). */
export const GRADOS_DE_CAUSALIDAD = [
  { value: 'A', label: 'A · probable' },
  { value: 'B', label: 'B · posible' },
  { value: 'O', label: 'O · no clasificable' },
  { value: 'O1', label: 'O1 · no concluyente' },
  { value: 'N', label: 'N · improbable' },
] as const;

/** HU-15-C1: cómo se lee cada clave del criterio de gravedad (RN-03). */
export const ETIQUETAS_DEL_CRITERIO: Record<string, string> = {
  muerte: 'Muerte',
  peligro_para_la_vida: 'Peligro para la vida',
  ingreso_o_prolongacion_centro_veterinario: 'Ingreso o prolongación en centro veterinario',
  discapacidad_significativa_o_persistente: 'Discapacidad significativa o persistente',
  anomalia_congenita: 'Anomalía congénita',
  interrupcion_de_la_gestacion: 'Interrupción de la gestación',
  signos_permanentes_o_prolongados: 'Signos permanentes o prolongados',
};

export const etiquetaDelCriterio = (clave: string): string =>
  ETIQUETAS_DEL_CRITERIO[clave] ?? clave.replaceAll('_', ' ').replace(/^\w/, (c) => c.toUpperCase());

const base = (casoId: string) => `/evaluacion/${segUuid(casoId)}`;

export function verEvaluacion(casoId: string) {
  return pedir<Evaluacion>(base(casoId));
}

/**
 * HU-12: guardado PARCIAL — se envía lo que se acaba de responder, no las 28 cada vez.
 *
 * DOS LISTAS, PORQUE «AUSENTE» NO PUEDE SIGNIFICAR «BÓRRALO» (Bug 892). El guardado es
 * parcial a propósito: dos miembros del Comité trabajan el mismo caso a la vez en una
 * reunión, y si lo ausente se borrara, el segundo en guardar aniquilaría las respuestas
 * del primero. Por eso `respuestas` es «lo que respondo o actualizo» y el retiro va
 * aparte y EXPLÍCITO en `puntos_retirados`: solo se borra lo que alguien mandó borrar.
 *
 * El servidor rechaza con 422 las dos listas vacías (guardado sin contenido) y un punto
 * que aparezca en ambas (contradicción del cliente); la pantalla no construye ninguno de
 * los dos casos.
 */
export function responderInstructivo(
  casoId: string,
  respuestas: { punto: number; valor: ValorDeRespuesta; observacion?: string | null }[],
  puntosRetirados: number[] = [],
) {
  return pedir<Evaluacion>(`${base(casoId)}/instructivo`, {
    metodo: 'PATCH',
    cuerpo: { respuestas, puntos_retirados: puntosRetirados },
  });
}

/** HU-12-C8: `puntos` vacío = todos los que faltan. La razón es obligatoria. */
export function marcarEnBloque(casoId: string, puntos: number[], razon: string) {
  return pedir<Evaluacion>(`${base(casoId)}/instructivo/marcado-en-bloque`, { metodo: 'POST', cuerpo: { puntos, razon } });
}

/** HU-12-C9. */
export function deshacerMarcado(casoId: string, razon: string) {
  return pedir<Evaluacion>(`${base(casoId)}/instructivo/deshacer-marcado`, { metodo: 'POST', cuerpo: { razon } });
}

/** HU-12-C1/C2: sin `algoritmo`, el servidor copia el predeterminado vigente (RN-18). */
export function registrarCausalidad(casoId: string, cuerpo: {
  grado: string; justificacion: string; algoritmo?: string | null; evaluado_por: string; conclusiones?: string | null;
}) {
  return pedir<Evaluacion>(`${base(casoId)}/causalidad`, { metodo: 'PUT', cuerpo });
}

export function guiaDeGravedad(casoId: string) {
  return pedir<GuiaDeGravedad>(`${base(casoId)}/gravedad/guia`);
}

/** HU-15: `justificacion_del_cambio` solo cuando ya había una gravedad decidida (C9). */
export function decidirGravedad(casoId: string, cuerpo: {
  gravedad: 'grave' | 'no_grave'; sustento: string; decidida_por: string; criterios: string[]; justificacion_del_cambio?: string | null;
}) {
  return pedir<Evaluacion>(`${base(casoId)}/gravedad`, { metodo: 'PUT', cuerpo });
}

export function listarAlgoritmos() {
  return pedir<{ codigo: string; nombre: string; version: string; estado: string; activo: boolean }[]>('/evaluacion/algoritmos');
}

// ---- HU-43 · especialistas ------------------------------------------------------------

export function sugerirEspecialistas(casoId: string) {
  return pedir<EspecialistaSugerido[]>(`${base(casoId)}/especialistas/sugeridos`);
}

export function listarInvitaciones(casoId: string) {
  return pedir<Invitacion[]>(`${base(casoId)}/especialistas`);
}

/**
 * BUG-902 (AT S3 §2.2.3): la respuesta de invitar, y solo de invitar. Con la persona ya
 * invitada y vigente, `ya_estaba_invitado = true` y `texto` dice desde cuándo y que no salió
 * correo: la pantalla lo muestra tal cual. En una invitación nueva va `false` con `texto` nulo.
 * Opcionales: un despliegue anterior a BUG-902 no los manda, y sin ellos es una invitación nueva.
 */
export type InvitacionRegistrada = Invitacion & { ya_estaba_invitado?: boolean; texto?: string | null };

// `invitado_por` SIGUE en el cuerpo: el AT S3 §2.0 retira los `*_por` de los cuerpos que
// enumera y `InvitacionEntrada` no está entre ellos (el esquema vigente lo exige). Duda
// abierta en `ui/MG-ILE-902.md`, Vacíos 3.
export function invitarEspecialista(casoId: string, cuerpo: { nombre: string; invitado_por: string; correo?: string | null; especialista_id?: string | null }) {
  return pedir<InvitacionRegistrada>(`${base(casoId)}/especialistas`, { metodo: 'POST', cuerpo });
}

export function retirarEspecialista(casoId: string, invitacionId: string) {
  return pedir<Invitacion>(`${base(casoId)}/especialistas/${segUuid(invitacionId)}`, { metodo: 'DELETE' });
}

export function marcarParticipacion(casoId: string, invitacionId: string, participo: boolean) {
  return pedir<Invitacion>(`${base(casoId)}/especialistas/${segUuid(invitacionId)}/participacion`, { metodo: 'PATCH', cuerpo: { participo } });
}

export function listarAportes(casoId: string) {
  return pedir<Aporte[]>(`${base(casoId)}/aportes`);
}

/** HU-43-C9. */
export function observarComoEspecialista(casoId: string, texto: string) {
  return pedir<Aporte>(`${base(casoId)}/aportes/observacion`, { metodo: 'POST', cuerpo: { texto } });
}

/** HU-43-C10. `contenidoBase64` sin prefijo `data:`. RN-09: `contieneDatoDePersonas` marca el documento como L2. */
export function adjuntarComoEspecialista(casoId: string, nombreArchivo: string, contenidoBase64: string, contieneDatoDePersonas = false) {
  return pedir<Aporte>(`${base(casoId)}/aportes/documento`, {
    metodo: 'POST',
    cuerpo: { nombre_archivo: nombreArchivo, contenido_base64: contenidoBase64, contiene_dato_de_personas: contieneDatoDePersonas },
  });
}

export function descargarAporte(casoId: string, aporteId: string) {
  return pedirBinario(`${base(casoId)}/aportes/${segUuid(aporteId)}/archivo`);
}

/**
 * HU-43-C10 · el AUTOR retira su aporte (22-sep-2026).
 *
 * No borra: el aporte deja de estar a disposición del Comité y queda el registro de que se
 * hizo y de que se retiró, con la fecha. El `motivo` es opcional —`null` cuando no se
 * escribe—; exigirlo convertiría en interrogatorio lo que es un derecho del que aportó.
 * Solo el autor puede: el permiso viaja en `puede_retirarlo` de cada aporte.
 */
export function retirarAporte(casoId: string, aporteId: string, motivo: string | null = null) {
  return pedir<Aporte>(`${base(casoId)}/aportes/${segUuid(aporteId)}/retiro`, { metodo: 'PATCH', cuerpo: { motivo } });
}

/** HU-43: lo único que ve un especialista al entrar. */
export function misInvitaciones() {
  return pedir<InvitacionPropia[]>('/evaluacion/invitaciones/mias');
}

// ---- HU-13 · acciones inmediatas y preventivas ---------------------------------------

/** HU-13-C2: los tres tipos, exactamente. Son el catálogo `tipo_accion` (TT-1); se escriben
 *  aquí porque el FAR-03 parte el informe por ellos y la pantalla no debe ofrecer otros. */
export const TIPOS_DE_ACCION = [
  { value: 'inmediata', label: 'Inmediata' },
  { value: 'preventiva', label: 'Preventiva' },
  { value: 'correctiva', label: 'Correctiva' },
] as const;

export const ESTADOS_DE_ACCION = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'en_curso', label: 'En curso' },
  { value: 'cumplida', label: 'Cumplida' },
] as const;

/** HU-13-C1/C5: `clase` (del catálogo `clase_accion`) o `descripcion` («Otra acción»): la lista es abierta. */
export function agregarAccion(casoId: string, cuerpo: {
  tipo: string; responsable: string; plazo: string; clase?: string | null; descripcion?: string | null; observacion?: string | null;
}) {
  return pedir<Evaluacion>(`${base(casoId)}/acciones`, { metodo: 'POST', cuerpo });
}

/** HU-13-C3: se mueve UNA; las demás siguen donde estaban. */
export function cambiarEstadoDeAccion(casoId: string, accionId: string, estado: string) {
  return pedir<Evaluacion>(`${base(casoId)}/acciones/${segUuid(accionId)}/estado`, { metodo: 'PATCH', cuerpo: { estado } });
}

export function quitarAccion(casoId: string, accionId: string) {
  return pedir<Evaluacion>(`${base(casoId)}/acciones/${segUuid(accionId)}`, { metodo: 'DELETE' });
}

// ---- HU-14 · el informe FAR-03 ---------------------------------------------------------

/** HU-14-C2: una de las OCHO secciones, en el orden del procedimiento. */
export type SeccionDelInforme = {
  numero: number;
  clave: string;
  titulo: string;
  texto: string | null;
  /** C7/C8: solo Antecedentes, Demanda y Conclusión se editan dentro del informe. */
  editable: boolean;
  nota: string | null;
  filas: Record<string, unknown>[];
};

/** HU-14-C10: una versión ENVIADA a aprobación, congelada con su texto. */
export type VersionDelInforme = {
  version: number;
  enviado_por: string | null;
  enviado_en: string;
  contenido: { secciones: { numero: number; clave: string; titulo: string; texto: string | null }[]; plazo_interno?: string | null };
};

export type Informe = {
  caso_id: string;
  informe_id: string;
  codigo: string | null;
  estado: 'borrador' | 'en_revision' | 'observado' | 'aprobado';
  /** AT S3 §2.0 (R1): «En aprobación», «Observado»… La pantalla lo pinta; vacío con un servidor anterior. */
  texto_del_estado?: string;
  iteraciones: number;
  aprobado_por: string | null;
  fecha_aprobacion: string | null;
  observaciones: { iteracion: number; motivo: string; observado_por: string; observado_en: string; subsanado_en: string | null; subsanado_por: string | null; que_se_subsano: string | null }[];
  tope_de_iteraciones: number | null;
  escalado_a_gerencia: boolean;
  version: number;
  secciones: SeccionDelInforme[];
  es_editable: boolean;
  motivo_de_no_editable: string | null;
  /** HU-14-C6: «Plazo interno: cumplido (23 h hábiles)». */
  plazo_interno: string | null;
  elaborado_en: string | null;
  enviado_en: string | null;
  versiones: VersionDelInforme[];
  /** HU-49-C5: al abrir el informe de un grave sin ampliar. */
  aviso_de_ampliacion: string | null;
  // ---- AT S3 §2.2.3 (`InformeSalidaS3`). Opcionales para que un despliegue anterior no rompa.
  /** HU-17-C10: con el informe en revisión, el contenido CONGELADO de vN; es lo que lee el aprobador. */
  version_en_lectura?: VersionEnLectura | null;
  /**
   * HU-17-C9 · lo que ESTA sesión puede hacer con el informe ahora, por su rol y el estado
   * del informe (`enviar`, `aprobar`, `observar`, `subsanar`, `redactar`). Los botones salen
   * de aquí (§8.1 R4); sin el campo no se ofrece ninguna acción.
   */
  acciones_disponibles?: AccionDelInforme[];
  /** HU-17-C4: «¿Aprobar el informe PE-231-2026 v2?». */
  texto_de_confirmacion_de_aprobacion?: string | null;
  /** HU-17-C6: «El caso pasa a notificación regulatoria» o «El caso pasa a seguimiento». */
  texto_del_efecto_de_aprobar?: string | null;
  /** HU-18-C3: una fila por ronda, con el texto que arma el servidor. */
  rondas?: RondaDelInforme[];
  /** HU-18-C5/C6: «Escalado a Gerencia General: 4 rondas, tope 3» o «Sin tope de rondas configurado». */
  texto_del_escalamiento?: string | null;
  /** HU-40: la constancia de cada aprobación y observación, atada a su versión. */
  constancias?: ConstanciaDelInforme[];
};

/** Las acciones del informe que el servidor puede ofrecer (AT S3 §2.2.3, `acciones_disponibles`). */
export type AccionDelInforme = 'enviar' | 'aprobar' | 'observar' | 'subsanar' | 'redactar';

/** HU-17-C10: la versión congelada al enviar, cada sección como la guardó el servidor. */
export type VersionEnLectura = {
  version: number;
  congelada_en: string;
  secciones: { numero?: number; clave?: string; titulo?: string; texto?: string | null; filas?: Record<string, unknown>[] }[];
};

/** HU-18-C3: «Ronda 1 · J. Salas · 06/10/2026 · Falta el lote · Subsanado por M. Díaz: …». */
export type RondaDelInforme = { ronda: number; texto: string };

/**
 * HU-40 (AT S3 §2.2.3, `ConstanciaSalidaS3`). Sin imagen de firma (C7). Quién y desde qué
 * equipo los toma el servidor de la sesión y del proxy de confianza (C8), nunca de la pantalla.
 */
export type ConstanciaDelInforme = {
  constancia_id: string;
  accion: string;
  documento_tipo: string;
  version: number;
  firmante_nombre: string;
  firmante_cargo: string | null;
  direccion_ip: string | null;
  motivo: string | null;
  ocurrido_en: string | null;
  /** «Aprobado por J. Salas, Gerente General · 07/10/2026 09:15 · versión 2». */
  texto: string;
};

/** ¿El servidor ofrece esta acción a esta sesión? Sin la lista, ninguna (§8.1 R4: no se adivina). */
export const ofrece = (informe: Pick<Informe, 'acciones_disponibles'> | null | undefined, accion: AccionDelInforme): boolean =>
  Boolean(informe?.acciones_disponibles?.includes(accion));

export const MENSAJE_SIN_CAUSALIDAD = 'Registra la causalidad antes de elaborar el informe';

export function verInforme(casoId: string) {
  return pedir<Informe>(`${base(casoId)}/informe`);
}

/** HU-14-C1: el informe nace en borrador; el caso pasa a en_aprobacion. */
export function elaborarInforme(casoId: string, elaboradoPor: string) {
  return pedir<Informe>(`${base(casoId)}/informe`, { metodo: 'POST', cuerpo: { elaborado_por: elaboradoPor } });
}

/** HU-14-C7: una de las tres secciones redactables. */
export function redactarInforme(casoId: string, seccion: 'antecedentes' | 'demanda' | 'conclusion', texto: string, redactadoPor: string) {
  return pedir<Informe>(`${base(casoId)}/informe/redaccion`, { metodo: 'PATCH', cuerpo: { seccion, texto, redactado_por: redactadoPor } });
}

// EL AUTOR SALE DE LA SESIÓN (AT S3 §2.0, tareas 17-2 y 18-2). Enviar, aprobar, observar y
// subsanar ya no llevan `enviado_por`, `aprobado_por`, `observado_por` ni `subsanado_por`:
// el servidor toma el usuario del token (y la IP del proxy de confianza para la constancia,
// HU-40-C8). La frontera tiene `extra="forbid"`, así que un cuerpo con esos campos responde
// 422 y no se registra nada: por eso estas cuatro funciones ya no reciben el autor.

/** HU-14-C9/C10: congela la versión y la pone en manos del aprobador. Sin cuerpo. */
export function enviarInformeAAprobacion(casoId: string) {
  return pedir<Informe>(`${base(casoId)}/informe/envio`, { metodo: 'POST' });
}

/** HU-18 / HU-14-C10: corregido el observado, reenviar es la versión siguiente (solo el Comité). */
export function subsanarInforme(casoId: string, queSeSubsano: string) {
  return pedir<Informe>(`${base(casoId)}/informe/subsanacion`, { metodo: 'POST', cuerpo: { que_se_subsano: queSeSubsano } });
}

// ---- HU-17 / HU-18 · la aprobacion del FAR-03 (rol aprobador) --------------------------

/** HU-17-C6: aprueba la versión en revisión. El código ya existe: es el número del caso (RN-07). Sin cuerpo. */
export function aprobarInforme(casoId: string) {
  return pedir<Informe>(`${base(casoId)}/informe/aprobacion`, { metodo: 'POST' });
}

/** HU-17-C7 / RN-04: observar exige motivo; el informe vuelve al Comité como «observado». */
export function observarInforme(casoId: string, motivo: string) {
  return pedir<Informe>(`${base(casoId)}/informe/observacion`, { metodo: 'POST', cuerpo: { motivo } });
}

// ---- HU-16 / HU-18 · la cola del aprobador -----------------------------------------------

/**
 * Una fila de `GET /evaluacion/informes`: un INFORME en manos del aprobador, con lo justo
 * del caso para reconocerlo. Los campos que el servidor puede no calcular (`plazo_interno`,
 * fechas de observación) van opcionales o nulos: la pantalla los pinta con «—».
 */
export type InformeEnAprobacion = {
  caso_id: string;
  numero_de_caso: string | null;
  pais_evento: string;
  nombre_pais: string | null;
  producto: string | null;
  gravedad: 'grave' | 'no_grave' | null;
  estado_del_caso: string | null;
  informe_id: string;
  /** Nulo solo en el repliegue a la bandeja, que no conoce la versión. */
  version: number | null;
  estado_del_informe: 'en_revision' | 'observado' | 'aprobado' | string;
  /** R1: el mismo texto que el detalle del informe (`informe.texto_del_estado`, ui/MG-ILE-43 «Coherencia»). */
  texto_del_estado_del_informe?: string;
  enviado_por: string | null;
  enviado_en: string | null;
  observado_en?: string | null;
  motivo_de_observacion?: string | null;
  ronda?: number | null;
  /** HU-14-C6: «cumplido (23 h hábiles)» / «vencido». `estado` es la clave; `texto`, lo que se lee. */
  plazo_interno?: { estado: string; texto: string } | null;
  accion_pendiente?: string | null;
};

export type EstadoDelInformeEnAprobacion = 'en_revision' | 'observado' | 'aprobado';

/** La fila tal como viaja: `plazo_interno` es TEXTO (`texto_del_plazo_interno` del backend). */
type InformeEnAprobacionEnElCable = Omit<InformeEnAprobacion, 'plazo_interno'> & {
  plazo_interno?: string | { estado: string; texto: string } | null;
};

/** `InformesEnAprobacionSalida`: la lista envuelta, con su total y la hora de consulta. */
export type InformesEnAprobacion = { informes: InformeEnAprobacionEnElCable[]; total: number; consultada_en: string };

/**
 * HU-14-C6: el backend manda el plazo interno como texto —«Plazo interno: cumplido (3 h
 * hábiles)» / «Plazo interno: vencido (30 h hábiles)»— y la pantalla lo pinta como insignia
 * con tono, que necesita la clave. La clave se deriva del texto, que es el contrato real, y
 * el texto pierde el prefijo porque la columna ya se llama «Plazo interno». Sin esto la
 * insignia salía vacía y gris (dev, 17-sep-2026).
 */
export function plazoInternoComoInsignia(plazo: string | { estado: string; texto: string } | null | undefined): { estado: string; texto: string } | null {
  if (!plazo) return null;
  if (typeof plazo === 'object') return plazo;
  const texto = plazo.replace(/^Plazo interno:\s*/i, '');
  return {
    estado: /vencid/i.test(texto) ? 'vencido' : 'cumplido',
    texto: texto.charAt(0).toUpperCase() + texto.slice(1),
  };
}

/**
 * HU-16 / HU-18: la cola del aprobador YA NO ES UNA LECTURA DE LA BANDEJA DEL COMITÉ
 * (14-sep-2026). `GET /evaluacion/bandeja` solo trae los casos que el Comité tiene entre
 * manos; un informe en revisión cuyo caso salió de esa bandeja no aparecía, y el
 * aprobador no lo veía. Este endpoint lista INFORMES, por su estado. Sin `estado` trae
 * en_revision + observado, que es lo que la pantalla necesita.
 */
export async function listarInformesEnAprobacion(estado?: EstadoDelInformeEnAprobacion): Promise<InformeEnAprobacion[]> {
  const ruta = estado ? `/evaluacion/informes?estado=${estado}` : '/evaluacion/informes';
  // La respuesta viene ENVUELTA, como la bandeja del Comité: `{ informes, total,
  // consultada_en }` (`InformesEnAprobacionSalida` del backend). La pantalla trabaja con la
  // lista, así que se desenvuelve aquí. Este adaptador la declaraba como arreglo y el
  // servidor mandaba el objeto: «(intermediate value).filter is not a function» y la cola
  // de aprobación no cargaba (dev, 17-sep-2026). No lo vio nadie porque la prueba unitaria
  // simula este adaptador y la E2E aprueba por API.
  const respuesta = await pedir<InformesEnAprobacion>(ruta);
  return respuesta.informes.map((f) => ({ ...f, plazo_interno: plazoInternoComoInsignia(f.plazo_interno) }));
}

// ---------------------------------------------------------------------------
// HU-49 — ampliar el informe del EA grave (actividad 8)
// ---------------------------------------------------------------------------

/**
 * C2/C4 · UN archivo de una entrada del sustento (22-sep-2026).
 *
 * Antes el archivo vivía DENTRO de la entrada y por eso solo cabía uno: elegir otro
 * reemplazaba al anterior en silencio. Ahora el anexo es una cosa con identidad propia
 * (`id`), y una cita puede llevar varios — como «Adjuntar» en «Registrar caso».
 *
 * `numero_de_anexo` es PLANO a través de todas las entradas (Anexo 1, 2, 3…), que es como
 * el FAR-03 los cita; por eso lo calcula el servidor y no la pantalla, y por eso borrar uno
 * devuelve la ampliación entera: los que van detrás se renumeran.
 */
export type AnexoDeSustento = {
  id: string;
  numero_de_anexo: number;
  /** «Anexo 1: laboratorio_L-2026-08.pdf», tal como se cita en el informe. */
  etiqueta: string;
  nombre_archivo: string;
  tipo_mime: string;
  tamano_bytes: number;
  contiene_dato_de_personas: boolean;
};

export type ReferenciaTecnica = {
  orden: number;
  /** La IDENTIDAD de la entrada. `null` solo en una entrada recién añadida en pantalla y todavía sin guardar. */
  id: string | null;
  cita: string;
  tipo: string | null;
  anexos: AnexoDeSustento[];
  subido_por: string | null;
};

/** C8: un punto del instructivo con la respuesta de HU-12, en solo lectura. */
export type RespuestaDelNumeral = {
  numero: number;
  corto: string;
  texto: string;
  valor: string | null;
  observacion: string | null;
  derivada: boolean;
  observacion_protegida: boolean;
};

export type Ampliacion = {
  caso_id: string;
  ampliacion_id: string;
  factores: string | null;
  informacion_adicional: string | null;
  evidencias_revisadas: string[];
  referencias: ReferenciaTecnica[];
  registrado_por: string | null;
  fecha: string | null;
  titulo_del_numeral: string;
  respuestas_del_instructivo: RespuestaDelNumeral[];
  respondidos: number;
  total: number;
  esta_registrada: boolean;
  /** C7: si editar la ampliación reabrió el FAR-03 aprobado como versión nueva. */
  informe_reabierto_en_version: number | null;
  /**
   * C2 · cuántos anexos caben en UNA entrada, leído de `parametro` como el resto de los
   * límites. Si el servidor no lo manda, la pantalla NO pre-valida el cupo: contesta él.
   * Un número escrito aquí anunciaría un tope que no es el de la base.
   */
  anexo_cantidad_maxima?: number;
  anexo_cantidad_minima?: number;
  /**
   * BUG-O09-C1 (AT S3 §2.2.3): con el FAR-03 aprobado, la confirmación que se pide ANTES de
   * adjuntar o quitar un anexo («Adjuntar con el FAR-03 aprobado abre la versión 2 en
   * borrador»). Nulo con el informe en cualquier otro estado: entonces no se pregunta.
   */
  texto_de_confirmacion_de_anexo?: string | null;
  /** BUG-O09: en la respuesta de adjuntar o quitar, la versión del FAR-03 que el acto reabrió y su texto. */
  informe_reabierto?: { version: number; texto: string } | null;
  /**
   * BUG-898-C4 (AT S3 §2.2.3): «El informe está en aprobación: la ampliación se edita cuando
   * se observe o se apruebe». Opcional: lo trae la tarea 898-2 del backend.
   */
  texto_de_solo_lectura?: string | null;
};

/**
 * Una entrada del sustento tal como se ENVÍA en el PUT. Desde el 22-sep-2026 los archivos
 * NO viajan aquí: son anexos con identidad propia y van por `adjuntarAnexo` / `quitarAnexo`,
 * uno por petición. Lo que este PUT decide es qué ENTRADAS hay y qué dice cada una; una
 * entrada cuyo `id` no venga en la lista se borra, y con ella sus anexos.
 */
export type EntradaDeSustento = {
  /** `null` para una entrada nueva; el servidor le asigna su `id` y lo devuelve. */
  id: string | null;
  cita: string;
  tipo?: string | null;
};

export const AVISO_FALTA_AMPLIACION = 'Falta la información adicional de la actividad 8';
export const TITULO_DEL_NUMERAL_910 = 'Información del numeral 9.10 (Res. 2528)';

export function verAmpliacion(casoId: string) {
  return pedir<Ampliacion>(`${base(casoId)}/ampliacion`);
}

/** C1/C2/C3/C7: la lista de sustento se manda ENTERA; los archivos van aparte (`adjuntarAnexo`). */
export function ampliarInforme(casoId: string, cuerpo: {
  registrado_por: string; factores?: string | null; informacion_adicional?: string | null;
  evidencias_revisadas?: string[]; referencias: EntradaDeSustento[];
}) {
  return pedir<Ampliacion>(`${base(casoId)}/ampliacion`, { metodo: 'PUT', cuerpo });
}

/**
 * C2 · adjunta UN archivo a una entrada YA GUARDADA. Un archivo por petición, como el
 * documento del especialista: así el rechazo de uno no arrastra a los demás y el servidor
 * cuenta el cupo de la entrada en cada llegada.
 *
 * `referenciaId` nunca es nulo: un anexo cuelga de una entrada que existe. Si la entrada
 * todavía no se guardó, la pantalla guarda primero y usa el `id` que devuelve el PUT.
 * `contenidoBase64` va sin el prefijo `data:`. RN-09: `contieneDatoDePersonas` lo marca L2.
 *
 * DEVUELVE LA AMPLIACIÓN ENTERA, no el anexo suelto (22-sep-2026), por lo mismo que el
 * borrado: la numeración es PLANA a través de todas las citas, así que colgar un archivo de
 * la primera corre los números de las siguientes. Con el anexo suelto la pantalla se
 * quedaba pintando etiquetas desfasadas.
 */
export function adjuntarAnexo(casoId: string, referenciaId: string, nombreArchivo: string, contenidoBase64: string, tipoMime: string | null = null, contieneDatoDePersonas = false) {
  return pedir<Ampliacion>(`${base(casoId)}/ampliacion/anexos`, {
    metodo: 'POST',
    cuerpo: {
      referencia_id: referenciaId,
      nombre_archivo: nombreArchivo,
      contenido_base64: contenidoBase64,
      tipo_mime: tipoMime,
      contiene_dato_de_personas: contieneDatoDePersonas,
    },
  });
}

/** C2: quita un anexo. Devuelve la ampliación ENTERA porque la numeración plana se recalcula. */
export function quitarAnexo(casoId: string, anexoId: string) {
  return pedir<Ampliacion>(`${base(casoId)}/ampliacion/anexos/${segUuid(anexoId)}`, { metodo: 'DELETE' });
}

/**
 * HU-49-C10 / BUG-O09-C2: marca o desmarca «Contiene datos de personas» en un anexo YA
 * SUBIDO, sin volver a subirlo. No reabre el FAR-03 aprobado: solo cambia la marca L2.
 * Devuelve la ampliación entera, como adjuntar y quitar.
 */
export function reclasificarAnexo(casoId: string, anexoId: string, contieneDatoDePersonas: boolean) {
  return pedir<Ampliacion>(`${base(casoId)}/ampliacion/anexos/${segUuid(anexoId)}/clasificacion`, {
    metodo: 'PATCH',
    cuerpo: { contiene_dato_de_personas: contieneDatoDePersonas },
  });
}

/** C2/C10: el archivo de un anexo; uno L2 responde 403 sin permiso, y el intento queda auditado. */
export function descargarAnexo(casoId: string, anexoId: string) {
  return pedirBinario(`${base(casoId)}/ampliacion/anexos/${segUuid(anexoId)}/archivo`);
}
